import React from 'react';
import { HelpCircle, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const FAQ = () => {
  const faqs = [
    {
      question: 'What sizes do you offer?',
      answer: 'We offer two sizes: 12x30 (360 sq ft) and 14x40 (560 sq ft) gravel spaces. Both sizes are perfect for boats, RVs, trailers, and business equipment.'
    },
    {
      question: 'Is the facility secure?',
      answer: 'Yes! We have a gated facility with a fenced perimeter. Only tenants receive access codes to enter the property.'
    },
    {
      question: 'What are your access hours?',
      answer: '24/7! You can access your space anytime, day or night, 365 days a year. Perfect for early morning fishing trips or late-night equipment pickups.'
    },
    {
      question: 'Do you offer covered storage?',
      answer: 'We specialize in outdoor storage on gravel lots. This allows us to offer larger spaces at more affordable rates than covered storage.'
    },
    {
      question: 'Can I store a boat here?',
      answer: 'Absolutely! We\'re located minutes from Lake Texoma and Bois d\'Arc Lake, making us the perfect spot for boat storage. All boat sizes welcome.'
    },
    {
      question: 'Do you allow RVs and trailers?',
      answer: 'Yes! RVs, travel trailers, fifth wheels, and all types of trailers are welcome. Our wide gravel aisles make it easy to maneuver large vehicles.'
    },
    {
      question: 'Can businesses rent space?',
      answer: 'Definitely! We welcome commercial accounts for fleet vehicles, equipment storage, and business inventory. Month-to-month terms available.'
    },
    {
      question: 'Is there a long-term contract?',
      answer: 'No! We offer flexible month-to-month rentals with no long-term commitment required.'
    },
    {
      question: 'What type of surface are the lots?',
      answer: 'Our lots have a gravel surface that provides good drainage and all-weather access for your vehicles and equipment.'
    },
    {
      question: 'Do you have electricity hookups?',
      answer: 'These are storage spaces without electrical hookups. Contact us if you have specific power needs.'
    },
    {
      question: 'How do I make a payment?',
      answer: 'We accept various payment methods. Contact us for details on payment options and setting up automatic payments.'
    },
    {
      question: 'Can I reserve a space online?',
      answer: 'Contact us directly to check availability and reserve your space. Our team will help you find the perfect spot for your needs.'
    }
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="FAQ | VD Storage Bells - Bells, TX"
        description="Common questions about VD Storage Bells — access hours, security, boat & RV storage, payments, and policies. Serving Bells, Sherman, Denison, and Grayson County."
        canonical="/faq"
        keywords="storage FAQ Bells TX, boat storage questions, RV storage Grayson County, storage access hours"
        structuredData={faqSchema}
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="faq-title">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-green-100">
            Get answers about VD Storage Bells
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6" data-testid={`faq - ${index} `}>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span>{faq.question}</span>
                </h3>
                <p className="text-gray-700 ml-9">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Our team is here to help you find the perfect storage solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-md"
              data-testid="contact-button"
            >
              Contact Us
            </a>
            <a
              href="/storage-sizes"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 border-2 border-green-600 rounded-lg hover:bg-green-50 transition-colors"
              data-testid="sizes-button"
            >
              View Sizes & Rates
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;